import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { AuthUser, ComplianceStatusResponse } from '@fetmode/types';
import { useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { formatCoins } from '@fetmode/utils';
import { Button } from '@fetmode/ui';
import { Link } from 'react-router-dom';

import { useCoinShop } from '../wallet/index.js';

export const TIP_PRESETS = [5, 10, 25, 50] as const;

export interface TipModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AuthUser | null;
  compliance: ComplianceStatusResponse | null;
  canTip: boolean;
  coinBalance: number;
  tipAmount: string;
  onTipAmountChange: (value: string) => void;
  tipMessage: string;
  onTipMessageChange: (value: string) => void;
  isTipping: boolean;
  tipError: string | null;
  tipSuccess: boolean;
  onSend: () => void;
}

export function TipModal({
  isOpen,
  onClose,
  user,
  compliance,
  canTip,
  coinBalance,
  tipAmount,
  onTipAmountChange,
  tipMessage,
  onTipMessageChange,
  isTipping,
  tipError,
  tipSuccess,
  onSend,
}: TipModalProps): ReactElement | null {
  const { openAuthModal } = useAuthModal();
  const { requireCoins } = useCoinShop();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const parsedTipAmount = Number.parseInt(tipAmount, 10);
  const canSubmit =
    canTip && !isTipping && Number.isFinite(parsedTipAmount) && parsedTipAmount >= 1;

  return createPortal(
    <div className="show-room-modal" role="dialog" aria-modal="true" aria-labelledby="tip-modal-title">
      <button
        type="button"
        className="show-room-modal__backdrop"
        aria-label={t('common.show.tipModalClose')}
        onClick={onClose}
      />
      <div className="show-room-modal__panel">
        <header className="show-room-modal__header">
          <h2 id="tip-modal-title" className="show-room-modal__title">
            {t('common.show.tipModalTitle')}
          </h2>
          <button
            type="button"
            className="show-room-modal__close"
            onClick={onClose}
            aria-label={t('common.show.tipModalClose')}
          >
            ×
          </button>
        </header>

        <div className="show-room-modal__body">
          {!user ? (
            <p style={{ margin: 0 }}>
              <button
                type="button"
                className="show-room-modal__text-action"
                onClick={() => {
                  onClose();
                  openAuthModal({ mode: 'register', intent: 'tip' });
                }}
              >
                {t('common.show.tipSignIn')}
              </button>
            </p>
          ) : !compliance?.canAccess ? (
            <p style={{ margin: 0 }}>
              <Link to="/verify" onClick={onClose}>
                {t('common.show.tipVerify')}
              </Link>
            </p>
          ) : (
            <>
              <p className="show-room__tip-balance" style={{ marginTop: 0 }}>
                {t('common.show.tipBalance').replace('{{balance}}', formatCoins(coinBalance))}
              </p>

              <section className="show-room-modal__section">
                <h3 className="show-room-modal__section-title">{t('common.show.tipPresets')}</h3>
                <div className="show-room-modal__duration-grid">
                  {TIP_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className={`show-room-modal__duration${parsedTipAmount === preset ? ' show-room-modal__duration--active' : ''}`}
                      onClick={() => {
                        if (!requireCoins(coinBalance, preset, () => {
                          onTipAmountChange(String(preset));
                        })) {
                          return;
                        }
                        onTipAmountChange(String(preset));
                      }}
                    >
                      <strong>{formatCoins(preset)}</strong>
                    </button>
                  ))}
                </div>
              </section>

              <label style={{ display: 'block', marginTop: '1rem' }}>
                {t('common.show.tipCustom')}
                <input
                  className="fm-input"
                  type="number"
                  min={1}
                  value={tipAmount}
                  onChange={(event) => {
                    onTipAmountChange(event.target.value);
                  }}
                />
              </label>

              <label style={{ display: 'block', marginTop: '0.75rem' }}>
                {t('common.show.tipMessage')}
                <input
                  className="fm-input"
                  value={tipMessage}
                  maxLength={200}
                  onChange={(event) => {
                    onTipMessageChange(event.target.value);
                  }}
                />
              </label>

              {tipError ? <p className="fm-error" style={{ marginTop: '0.75rem' }}>{tipError}</p> : null}
              {tipSuccess ? (
                <p style={{ color: 'var(--fm-color-link)', marginTop: '0.75rem', marginBottom: 0 }}>
                  {t('common.show.tipSuccess')}
                </p>
              ) : null}

              <div style={{ marginTop: '1rem' }}>
                <Button
                  label={
                    isTipping
                      ? t('common.show.tipSending')
                      : t('common.show.tipSend').replace(
                          '{{amount}}',
                          formatCoins(Number.isFinite(parsedTipAmount) ? parsedTipAmount : 0),
                        )
                  }
                  disabled={!canSubmit}
                  onClick={() => {
                    if (
                      !Number.isFinite(parsedTipAmount) ||
                      !requireCoins(coinBalance, parsedTipAmount, () => {
                        onSend();
                      })
                    ) {
                      return;
                    }
                    onSend();
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
